<!-- Misconduct_Referee.vue -->
<template>
    <div class="cl-page">
    <div class="cl-pageheader">
  <div class="cl-pageheader-title">Supplemental Report Management</div>
  <div class="RL_note">List of misconduct issued by the referee. Click on a card for more details.</div>

  <div class="gridWrapper">
      <div class="gridContainer">
          <div class="gridHeaders">
              <div class="gridItem date">Date</div>
              <div class="gridItem genderAge">Gender/Age</div>
              <div class="gridItem teams">Teams</div>
              <div class="gridItem player">Player</div>
              <div class="gridItem misc-item">Misconduct</div>
              <div class="gridItem incident-description">Incident</div>
              <div class="gridItem actions">PDF</div>
          </div>
          <div class="gridContent">
              <ul v-if="redCards.length > 0">
                  <li v-for="card in redCards" :key="card.id" class="gridRow" 
                    :class="{
                    yellowCard: card.RefereeAction === 'Yellow Card',
                    redCard: card.RefereeAction === 'Red Card',
                    purpleCard: card.RefereeAction === 'Purple Card'}">
                      <div class="gridItem date">{{ formatGameDate(card.GameDate) }}</div>
                      <div class="gridItem genderAge">{{ card.GenderAge }}</div>
                      <div class="gridItem teams">{{ card.Teams }}</div>
                      <div class="gridItem player">
                        <div class="player-info">
                          <span class="time">{{ card.Time }}'</span>
                          <span class="player-name">({{ card.Offender }}) #{{ card.PlayerNumber }} {{ card.PlayerNameLast }}, {{ card.PlayerNameFirst }}</span>
                        </div>
                      </div>
                      <div class="gridItem misc-item">{{ card.MisconductItem || 'No misconduct data' }}</div>
                      <div class="gridItem incident-description">{{ card.IncidentDescription }}</div>
                      <div class="gridItem actions">
                          <GeneratePDF :card="card">
                              <span class="material-icons pdf-icon"> picture_as_pdf </span>
                          </GeneratePDF>
                      </div>
                  </li>
              </ul>
              <div v-else class="no-data">
                <p>No misconduct data available</p>
              </div>
          </div>
      </div>
  </div>
  </div>
  </div>
</template>

<script>
import GeneratePDF from "../components/GeneratePDF.vue";
import { misconductColRef, matchreportsColRef } from "../firebase";
import { query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { ref, onMounted } from "vue";

export default {
  name: "Misconduct_Referee",
  components: { GeneratePDF },
  setup() {
      const redCards = ref([]);
      redCards.value = [];

      onMounted(async () => {
          const auth = getAuth();
          const user = auth.currentUser;

          if (!user) {
              console.log("User is not authenticated");
              return;
          }
          console.log("user ", user.uid)
          await loadCards(user.uid);
      });

      const loadCards = async (userId) => {
        try {
          const qRC = query(
            misconductColRef,
            where("MatchReportUserID", "==", userId),
            where("RefereeAction", "in", ["Yellow Card", "Red Card", "Purple Card"])
          );
          const qRCSnapshot = await getDocs(qRC);

          if (qRCSnapshot.empty) {
            console.log("No cards found.");
            return;
          }

          const misconductDocsRaw = qRCSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          // Only keep misconducts with valid MatchReportId
          const validMisconducts = misconductDocsRaw.filter(m =>
            m.MatchReportID && typeof m.MatchReportID === 'string' && m.MatchReportID.trim() !== ''
          );

          // Fetch match reports in parallel
          const matchReportPromises = validMisconducts.map(m =>
            getDoc(doc(matchreportsColRef, m.MatchReportID))
          );
          const matchReports = await Promise.all(matchReportPromises);

          redCards.value = [];

          validMisconducts.forEach((misconduct, index) => {
            const matchReportDoc = matchReports[index];
            const matchReportData = matchReportDoc?.exists() ? matchReportDoc.data() : {};

            redCards.value.push({
              id: misconduct.id,
              ...misconduct,
              GameDate: formatGameDate(matchReportData?.GameDate || misconduct.GameDate),
              GameNumber: matchReportData?.GameNumber || '—',
              LeagueTypeSeason: matchReportData?.LeagueTypeSeason || '—',
              TeamHome: matchReportData?.TeamHome || '—',
              TeamAway: matchReportData?.TeamAway || '—',
              ScoreHome: matchReportData?.ScoreHome ?? '',
              ScoreAway: matchReportData?.ScoreAway ?? '',
              Teams: matchReportData?.TeamHome && matchReportData?.TeamAway
                ? `${matchReportData.TeamHome} vs. ${matchReportData.TeamAway}`
                : 'Teams unknown',
              GenderAge: matchReportData?.GenderAge || misconduct.GenderAge || '—',
              REFName: matchReportData?.REFName || '—',
              AR1Name: matchReportData?.AR1Name || '—',
              AR2Name: matchReportData?.AR2Name || '—',
              REF4thName: matchReportData?.REF4thName || '—',
              YellowCardCount: matchReportData?.YellowCardCount ?? '',
              RedCardCount: matchReportData?.RedCardCount ?? '',
              LawReference: misconduct.LawReference || '',
            });
          });

        } catch (error) {
          console.error("Error loading red cards:", error);
        }
      };

      const formatGameDate = (gameDate) => {
          if (!gameDate) return "N/A";
          if (gameDate instanceof Object && typeof gameDate.toDate === "function") {
              const date = gameDate.toDate();
              return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${String(date.getFullYear()).slice(-2)}`;
          }
          return gameDate;
      };

      return {
          redCards,
          formatGameDate,
      };
  },
};
</script>
  
<style scoped>
/* Responsive Reset and Base Styles */
.cl-page {
  min-height: 100vh;
  background: radial-gradient(1200px 600px at 20% 0%, #eac891 0%, #eac891 40%, #f2d9ad 100%);
  padding-top: 80px; /* Account for any top navigation */
}

/* Header - Responsive */
.cl-pageheader {
  position: sticky;
  top: 0;
  height: auto;
  min-height: 60px;
  width: 100%;
  padding: clamp(10px, 2vw, 20px);
  border-top: 5px solid #02b8cc;
  border-bottom: 1px solid #32e3f7;
  background-color: #e78d58;
  color: #000;
  z-index: 100;
  box-sizing: border-box;
}

.cl-pageheader-title {
  color: #000;
  font-size: clamp(1.2rem, 3vw, 1.5rem);
  font-weight: bold;
  margin-bottom: 8px;
}

/* Note - Responsive */
.RL_note {
  font-size: clamp(0.9rem, 2vw, 1rem);
  line-height: 1.4;
  background-color: rgba(255, 255, 255, 0.8);
  padding: clamp(8px, 1vw, 12px);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 20px;
}

/* Grid Wrapper */
.gridWrapper * {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* Grid Container - Responsive */
.gridContainer {
  margin: 20px;
  padding: 0;
  overflow-x: auto; /* Allow horizontal scrolling on small screens */
  overflow-y: auto;
  font-size: clamp(11px, 2vw, 13px);
  max-height: calc(100vh - 280px); /* Dynamic height based on viewport */
}

/* Grid Headers - Responsive */
.gridHeaders {
  display: grid;
  grid-template-columns: 
    minmax(80px, 120px)    /* Date */
    minmax(100px, 140px)   /* Gender/Age */
    minmax(200px, 350px)   /* Teams */
    minmax(250px, 400px)   /* Player */
    minmax(150px, 250px)   /* Misconduct */
    minmax(200px, 400px)   /* Incident Description */
    minmax(60px, 80px);    /* Actions */
  gap: clamp(5px, 1vw, 10px);
  padding: clamp(8px, 1vw, 12px);
  background-color: #e9ecef;
  margin-bottom: 4px;
  min-width: 1100px; /* Minimum width to prevent squishing */
  font-weight: 600;
}

/* Grid Content */
.gridContent ul {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

/* Grid Row - Responsive */
.gridRow {
  display: grid;
  grid-template-columns: 
    minmax(80px, 120px)    /* Date */
    minmax(100px, 140px)   /* Gender/Age */
    minmax(200px, 350px)   /* Teams */
    minmax(250px, 400px)   /* Player */
    minmax(150px, 250px)   /* Misconduct */
    minmax(200px, 400px)   /* Incident Description */
    minmax(60px, 80px);    /* Actions */
  gap: clamp(5px, 1vw, 10px);
  padding: 0;
  margin-bottom: 4px;
  border-radius: 10px;
  background: #fff;
  font-size: clamp(10px, 2vw, 12px);
  min-height: 50px;
  align-items: center;
  min-width: 1100px; /* Match header minimum width */
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.gridRow:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0,0,0,0.15);
}

/* Grid Items */
.gridItem {
  padding: clamp(5px, 1vw, 8px);
  text-align: left;
  word-wrap: break-word;
  overflow-wrap: break-word;
  display: flex;
  align-items: center;
}

.gridItem.date { 
  justify-self: start; 
  font-weight: 600;
}
.gridItem.genderAge { 
  justify-self: start; 
  font-weight: 500;
}
.gridItem.teams { 
  justify-self: start; 
}
.gridItem.player { 
  justify-self: start; 
}
.gridItem.misc-item { 
  justify-self: start; 
}
.gridItem.incident-description { 
  justify-self: start; 
  line-height: 1.3;
}
.gridItem.actions { 
  justify-self: center; 
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Player Info Styling */
.player-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
}

.time {
  font-weight: 600;
  color: #005ce6;
  font-size: 0.9em;
}

.player-name {
  font-size: 0.85em;
  line-height: 1.2;
}

/* No Data State */
.no-data {
  padding: 40px 20px;
  text-align: center;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 10px;
  margin: 20px 0;
}

.no-data p {
  font-size: clamp(1rem, 2vw, 1.2rem);
  color: #666;
}

/* Material Icons - Responsive */
.material-icons {
  cursor: pointer;
  font-size: clamp(16px, 3vw, 20px);
  color: #888;
  transition: color 0.2s, transform 0.2s;
}

.material-icons:hover {
  transform: scale(1.1);
}

.material-icons.pdf-icon {
  background-color: white;
  border-radius: 50%;
  padding: clamp(4px, 1vw, 6px);
  color: #13033b;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: all 0.2s;
}

.material-icons.pdf-icon:hover {
  background-color: #f5f5f5;
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  color: #005ce6;
}

/* Card Type Styling */
.yellowCard {
  background-color: #fff9c4; /* light yellow */
  border-left: 4px solid #ffd600;
}

.redCard {
  background-color: #ffcdd2; /* light red */
  border-left: 4px solid #f44336;
}

.purpleCard {
  background-color: #f3e5f5; /* light purple */
  border-left: 4px solid #9c27b0;
}

/* Tablet Responsive Adjustments */
@media (max-width: 1200px) {
  .gridHeaders {
    grid-template-columns: 
      minmax(70px, 90px)    /* Date */
      minmax(80px, 110px)   /* Gender/Age */
      minmax(150px, 250px)  /* Teams */
      minmax(200px, 300px)  /* Player */
      minmax(120px, 180px)  /* Misconduct */
      minmax(150px, 250px)  /* Incident Description */
      minmax(50px, 60px);   /* Actions */
    min-width: 900px;
  }
  
  .gridRow {
    grid-template-columns: 
      minmax(70px, 90px)    /* Date */
      minmax(80px, 110px)   /* Gender/Age */
      minmax(150px, 250px)  /* Teams */
      minmax(200px, 300px)  /* Player */
      minmax(120px, 180px)  /* Misconduct */
      minmax(150px, 250px)  /* Incident Description */
      minmax(50px, 60px);   /* Actions */
    min-width: 900px;
    min-height: 45px;
  }
}

/* Mobile Responsive Adjustments */
@media (max-width: 768px) {
  .cl-pageheader {
    padding: 15px;
  }
  
  .cl-pageheader-title {
    text-align: center;
    font-size: 1.3rem;
  }
  
  .RL_note {
    font-size: 0.9rem;
    margin-bottom: 15px;
  }
  
  .gridContainer {
    margin: 15px;
    max-height: calc(100vh - 320px);
  }
  
  .gridHeaders {
    grid-template-columns: 
      minmax(60px, 70px)    /* Date */
      minmax(70px, 80px)    /* Gender/Age */
      minmax(120px, 180px)  /* Teams */
      minmax(150px, 200px)  /* Player */
      minmax(100px, 140px)  /* Misconduct */
      minmax(120px, 180px)  /* Incident Description */
      minmax(40px, 50px);   /* Actions */
    min-width: 750px;
    gap: 3px;
    padding: 6px;
    font-size: 10px;
  }
  
  .gridRow {
    grid-template-columns: 
      minmax(60px, 70px)    /* Date */
      minmax(70px, 80px)    /* Gender/Age */
      minmax(120px, 180px)  /* Teams */
      minmax(150px, 200px)  /* Player */
      minmax(100px, 140px)  /* Misconduct */
      minmax(120px, 180px)  /* Incident Description */
      minmax(40px, 50px);   /* Actions */
    min-width: 750px;
    gap: 3px;
    font-size: 10px;
    min-height: 40px;
  }
  
  .gridItem {
    padding: 4px;
  }
  
  .player-info {
    gap: 1px;
  }
  
  .time {
    font-size: 0.8em;
  }
  
  .player-name {
    font-size: 0.75em;
  }
  
  .material-icons {
    font-size: 14px;
  }
  
  .material-icons.pdf-icon {
    padding: 3px;
  }
}

/* Small Mobile Adjustments */
@media (max-width: 480px) {
  .gridHeaders {
    min-width: 650px;
    font-size: 9px;
  }
  
  .gridRow {
    min-width: 650px;
    font-size: 9px;
    min-height: 35px;
  }
  
  .cl-pageheader-title {
    font-size: 1.1rem;
  }
  
  .RL_note {
    font-size: 0.8rem;
  }
  
  .gridContainer {
    margin: 10px;
    max-height: calc(100vh - 340px);
  }
}

/* High DPI screens */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .material-icons {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
}

/* Print styles */
@media print {
  .cl-page {
    background: white;
    padding-top: 0;
  }
  
  .cl-pageheader {
    background: white;
    border: 1px solid black;
    color: black;
    position: static;
  }
  
  .RL_note {
    background: white;
    border: 1px solid #ccc;
  }
  
  .gridContainer {
    overflow: visible;
    max-height: none;
    margin: 10px;
  }
  
  .gridRow:hover {
    transform: none;
    box-shadow: none;
  }
  
  .yellowCard,
  .redCard,
  .purpleCard {
    background: white;
    border: 1px solid #ddd;
  }
  
  .material-icons.pdf-icon {
    display: none;
  }
}
</style>